import { createContext } from "react"
import { createStore } from "zustand"

import { Channel } from "@/config/channel"
import { trackAnalyticsEvent } from "@/lib/analytics"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
interface AnalyticsDataLayer {
  page_name: string
  conversion_pagename: string
  previous_pagename: string
  section: string
  subsection1: string
  subsection2: string
  page_template: string
  flusso_registrazione: string
  login_status: "logged" | "not-logged"
  site_id?: Channel
  webview: string
  url: string
  url_querystring: string
  intcmp: string
  operating_system: string
  welcome_bonus_name: string
  limite_ricarica: string
  documento_registrazione?: string
}
interface AnalyticsDataLayerInterface {
  dataLayer: AnalyticsDataLayer
}

type AnalyticsActionPayload = Partial<AnalyticsDataLayer> & Record<string, any>
interface AnalyticsState extends AnalyticsDataLayerInterface {
  setData: (data: AnalyticsActionPayload) => void
  sendData: (data: AnalyticsActionPayload) => void
}

type AnalyticsStore = ReturnType<typeof createAnalyticsStore>

/* -------------------------------------------------------------------------------------------------
 * helpers
 * -----------------------------------------------------------------------------------------------*/
const createAnalyticsStore = (initProps?: Partial<AnalyticsDataLayer>) => {
  const DEFAULT_DATA_LAYER: AnalyticsDataLayer = {
    page_name: "",
    conversion_pagename: "",
    previous_pagename: "",
    section: "registrazione",
    subsection1: "",
    subsection2: "",
    page_template: "",
    flusso_registrazione: "",
    login_status: "not-logged",
    webview: "",
    url: "",
    url_querystring: "",
    intcmp: "",
    operating_system: "",
    welcome_bonus_name: "",
    limite_ricarica: "",
  }

  return createStore<AnalyticsState>()((set) => ({
    dataLayer: { ...DEFAULT_DATA_LAYER, ...initProps },
    setData: (data) => {
      set((state) => {
        state.dataLayer = { ...state.dataLayer, ...data }
        return state
      })
    },
    sendData: (data) => {
      set((state) => {
        const {
          dataLayer: {
            welcome_bonus_name: DL_welcome_bonus_name = "",
            limite_ricarica: DL_limite_ricarica,
            documento_registrazione: DL_documento_registrazione,
            ...restDataLayer
          },
        } = state

        const page_name = data.page_name || restDataLayer.page_name
        const conversion_pagename =
          data.conversion_pagename ||
          data.page_name ||
          restDataLayer.conversion_pagename

        const flusso_registrazione =
          data.flusso_registrazione || restDataLayer.flusso_registrazione

        const url = window.location.origin + window.location.pathname
        const url_querystring = window.location.href

        const welcome_bonus_name =
          data.welcome_bonus_name || DL_welcome_bonus_name
        const limite_ricarica = data.limite_ricarica || DL_limite_ricarica
        const documento_registrazione =
          data.documento_registrazione || DL_documento_registrazione

        trackAnalyticsEvent({
          // in case we have loaded_page, we need to update previous_pagename
          // and pass the entire dataLayer
          ...(data.loaded_page && {
            ...restDataLayer,
            previous_pagename: restDataLayer.page_name,
            url,
            url_querystring,
          }),
          flusso_registrazione,
          conversion_pagename,
          ...(welcome_bonus_name && { welcome_bonus_name }),
          ...(documento_registrazione && { documento_registrazione }),
          ...(limite_ricarica && { limite_ricarica }),
          ...data,
        })

        state.dataLayer = {
          ...state.dataLayer,
          page_name,
          conversion_pagename,
          flusso_registrazione,
          ...(welcome_bonus_name && { welcome_bonus_name }),
          ...(documento_registrazione && { documento_registrazione }),
          ...(limite_ricarica && { limite_ricarica }),
        }
        return state
      })
    },
  }))
}

/* -------------------------------------------------------------------------------------------------
 * context
 * -----------------------------------------------------------------------------------------------*/
const AnalyticsStoreContext = createContext<AnalyticsStore | null>(null)

export { AnalyticsStoreContext, createAnalyticsStore }
export type { AnalyticsActionPayload, AnalyticsDataLayer, AnalyticsStore }
